import { useState, useEffect, useCallback } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaOptionsType } from 'embla-carousel'
import RoomThumbnail from './RoomThumbnail'
import "./RoomCarousel.css";

import { Image } from "../../AppDataType"
import ImageModal from '../ImageModal/ImageModal';
import { CaretIcon } from '../Icons/Icons';


interface Props {
    mainSlides: Image[];
    thumbsSlides: Image[];
    onImageLoad: () => void;
    options?: EmblaOptionsType;
}

const RoomCarousel: React.FC<Props> = ({ mainSlides, thumbsSlides, onImageLoad, options }) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options);
    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true
    });

    /* === ImageModal === */
    const [isModalImageOpen, setIsModalImageOpen] = useState(false);

    /* ================== */

    const onThumbClick = useCallback((index: number) => {
        if (!emblaMainApi || !emblaThumbsApi) return
            emblaMainApi.scrollTo(index)
        }, 
        [emblaMainApi, emblaThumbsApi]
    );

    const onSelect = useCallback(() => {
        if (!emblaMainApi || !emblaThumbsApi) return
        setSelectedIndex(emblaMainApi.selectedScrollSnap())
        emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap())
    }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

    useEffect(() => {
        if (!emblaMainApi) return
        onSelect()
        emblaMainApi.on('select', onSelect).on('reInit', onSelect)
    }, [emblaMainApi, onSelect]);


    /* === Room Carousel Buttons === */
    const handleNextSlide = () => {
        if (emblaMainApi?.canScrollNext()) {
            emblaMainApi.scrollNext();
        }
        else {
            emblaMainApi?.scrollTo(0, true);
        }
    }

    const handlePrevSlide = () => {
        if (emblaMainApi?.canScrollPrev()) {
            emblaMainApi.scrollPrev();
        }
        else {
            emblaMainApi?.scrollTo(mainSlides.length - 1, true);
        }
    }
    /* ============================= */


    return (
        <div className="room-carousel relative">
            <div className="room-carousel__viewport border-2 border-second-solid 
                            rounded-xl bg-second-solid" 
                 ref={emblaMainRef}>
                <div className="room-carousel__container">
                {
                    mainSlides.map((image, index) => (
                        <div className="room-carousel__slide" 
                             key={index}>
                            <figure className="room-carousel__slide__image cursor-pointer 
                                               transition-transform duration-500 ease-in-out 
                                               hover:scale-105"
                                    onClick={() => setIsModalImageOpen(true)}>
                                <img src={image.src}
                                     alt={image.alt}
                                     className="size-full object-cover"
                                     onLoad={onImageLoad}/>
                            </figure>
                        </div>
                    ))
                }
                </div>
            </div>

            <div className="room-carousel__thumbs">
                <div className="room-carousel__thumbs__viewport" 
                     ref={emblaThumbsRef}>
                    <div className="room-carousel__thumbs__container">
                    {
                        thumbsSlides.map((image, index) => (
                            <RoomThumbnail key={index} 
                                           selected={index === selectedIndex} 
                                           image={image} 
                                           onClick={() => onThumbClick(index)}/>
                        ))
                    }
                    </div>
                </div>
            </div>

            <div className="hidden lg:inline-block lg:absolute lg:bottom-7 lg:-left-20">
                <button className="room-carousel__button"
                        onClick={handlePrevSlide}>
                    <CaretIcon className="-rotate-90 size-5"/>
                </button>
            </div>

            <div className="hidden lg:inline-block lg:absolute lg:bottom-7 lg:-right-20">
                <button className="room-carousel__button"
                        onClick={handleNextSlide}>
                    <CaretIcon className="rotate-90 size-5"/>
                </button>
            </div>


            <ImageModal open={isModalImageOpen} 
                        onClose={() => setIsModalImageOpen(false)}>
                <img src={ mainSlides[selectedIndex]?.src }
                     alt={ mainSlides[selectedIndex]?.alt }/>
            </ImageModal>
        </div>
    )
}

export default RoomCarousel
